import { useState, useEffect } from 'react';
import { utcToUkTime } from '../utils/dateUtils';

interface CountdownTimerProps {
  targetDate: Date;
}

/**
 * CountdownTimer component displays a countdown to a target date
 * Automatically handles timezone conversion from UTC to UK time
 */
const CountdownTimer: React.FC<CountdownTimerProps> = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  // Re-calculate time left when component mounts and every second
  useEffect(() => {
    const calculateTimeLeft = () => {
      // Convert target date to UK timezone for proper comparison
      const ukTargetDate = utcToUkTime(targetDate);
      const difference = ukTargetDate.getTime() - new Date().getTime();
      
      if (difference <= 0) {
        return {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0
        };
      }

      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60)
      };
    };

    // Initial calculation
    setTimeLeft(calculateTimeLeft());

    // Set up interval to update every second
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    // Cleanup interval on unmount
    return () => clearInterval(timer);
  }, [targetDate]); // Only targetDate needs to be in deps array since we're recreating timer on each render

  const formatNumber = (num: number) => num.toString().padStart(2, '0');

  if (timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0) {
    return <span>Registration Closed</span>;
  }

  return (
    <div className="flex items-center space-x-1">
      <span className="countdown-clock">
        {timeLeft.days > 0 && `${timeLeft.days}d `}
        {formatNumber(timeLeft.hours)}h {formatNumber(timeLeft.minutes)}m {formatNumber(timeLeft.seconds)}s
      </span>
      <span className="text-xs opacity-75">remaining</span>
    </div>
  );
};

export default CountdownTimer;
